import { ApolloError, gql, useMutation, useQuery } from '@apollo/client';

type GraphqlTypename = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ['__typename']: string;
};

type BaseConfigFields = {
  firstOfferBelowTargetCounterDollars?: number;
  firstOfferBelowTargetCounterPercent?: number;
  autoRejectThreshold?: number;
  autoRejectThresholdPercent?: number;
  targetBuffer?: number;
  targetBufferPercent?: number;
  carrierNoBudgeBehavior?: 'DONT_BUDGE' | 'NEGOTIATE';
};

export type ABTestConfig = GraphqlTypename & {
  ['__typename']: 'ABTestConfigType';
  type: 'AB_TEST';
  variants: { strategyId: string; weight: number }[];
  assignmentMethod: string;
} & BaseConfigFields;

export type AcceptUpToMaxConfig = GraphqlTypename & {
  ['__typename']: 'AcceptUpToMaxConfigType';
  type: 'ACCEPT_UP_TO_MAX';
  fallbackStrategyId: string;
} & BaseConfigFields;

export type ConditionalRule = {
  strategyId: string;
  conditions: {
    carrierOfferOverMax?: boolean | null;
    offerRangePercentAtLeast?: number | null;
    carrierStoppedBudging?: boolean | null;
    commodityPatterns?: string[] | null;
    equipmentTypes?: string[] | null;
    carrierEquipmentTypes?: string[] | null;
    targetPriceAtLeast?: number | null;
    targetPriceAtMost?: number | null;
    maxPriceAtLeast?: number | null;
    maxPriceAtMost?: number | null;
    hotLoad?: {
      offerCountThreshold: number;
      timeWindowMinutes?: number | null;
    } | null;
    inquiryCountAtLeast?: number | null;
    inquiryCountAtMost?: number | null;
    inquiryTimeWindowMinutes?: number | null;
    offerCountAtLeast?: number | null;
    offerCountAtMost?: number | null;
    offerTimeWindowMinutes?: number | null;
    hasAnyOfferAbove?: number | null;
    hasAnyOfferBelow?: number | null;
    averageOfferAbove?: number | null;
    averageOfferBelow?: number | null;
    transferCountAtLeast?: number | null;
    transferCountAtMost?: number | null;
    transferTimeWindowMinutes?: number | null;
    hasRateGuidanceForMethod?: string | null;
    rateGuidanceAtLeast?: number | null;
    rateGuidanceAtMost?: number | null;
    pickupDateWithin?: string | null;
    pickupWithinHours?: number | null;
    pickupEndDateWithin?: string | null;
    pickupEndWithinHours?: number | null;
    rejectionCountAtLeast?: number | null;
    rejectionCountAtMost?: number | null;
    offerGreaterThanMaxPlus?: number | null;
  };
};

export type ConditionalConfig = GraphqlTypename & {
  ['__typename']: 'ConditionalConfigType';
  type: 'CONDITIONAL';
  rules: ConditionalRule[];
  defaultStrategyId: string;
} & BaseConfigFields;

export type EscalationConfig = GraphqlTypename & {
  ['__typename']: 'EscalationConfigType';
  type: 'ESCALATION';
  escalationThreshold: number;
  initialIncrementAmount: number;
  maxEscalationOverage?: number;
  timeoutSeconds: number;
  escalationMessage?: string;
  includeFields?: string[];
  escalationChannel: string;
} & BaseConfigFields;

export type IncrementTier = {
  carrierOfferOverMax?: boolean | null;
  targetPriceAtLeast?: number | null;
  targetPriceAtMost?: number | null;
  incrementAmount: number;
};

export type FixedAmountConfig = GraphqlTypename & {
  ['__typename']: 'FixedAmountConfigType';
  type: 'FIXED_AMOUNT';
  incrementAmount: number;
  incrementTiers?: IncrementTier[] | null;
} & BaseConfigFields;

export type NeverBudgeConfig = GraphqlTypename & {
  ['__typename']: 'NeverBudgeConfigType';
  type: 'NEVER_BUDGE';
} & BaseConfigFields;

export type AlwaysAcceptConfig = GraphqlTypename & {
  ['__typename']: 'AlwaysAcceptConfigType';
  type: 'ALWAYS_ACCEPT';
} & BaseConfigFields;

export type AlwaysDeclineConfig = GraphqlTypename & {
  ['__typename']: 'AlwaysDeclineConfigType';
  type: 'ALWAYS_DECLINE';
} & BaseConfigFields;

export type PercentageBasedConfig = GraphqlTypename & {
  ['__typename']: 'PercentageBasedConfigType';
  type: 'PERCENTAGE_BASED';
  incrementPercentage: number;
} & BaseConfigFields;

export type SmartPricingConfig = GraphqlTypename & {
  ['__typename']: 'SmartPricingConfigType';
  type: 'SMART_PRICING';
  failCostConstant?: number;
  maxSteps?: number;
  alphaPrior?: number;
  betaPrior?: number;
  timeDecay?: number;
  offerSamplingRate?: number;
  expectedOffersPerTruckToLoad?: number;
  requiresApprovalMessage?: string;
} & BaseConfigFields;

export type NegotiationStrategyConfig =
  | ABTestConfig
  | AcceptUpToMaxConfig
  | AlwaysAcceptConfig
  | AlwaysDeclineConfig
  | ConditionalConfig
  | EscalationConfig
  | FixedAmountConfig
  | NeverBudgeConfig
  | PercentageBasedConfig
  | SmartPricingConfig;

export type NegotiationStrategy = {
  id: string;
  name: string;
  type: string;
  config: NegotiationStrategyConfig;
  active: boolean;
};

type UseNegotiationStrategiesState = {
  strategies: NegotiationStrategy[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
};

type UseNegotiationStrategiesOptions = {
  organizationId?: string | null;
};

const NEGOTIATION_STRATEGY_CONFIG_FRAGMENT = gql`
  fragment NegotiationStrategyConfigFields on NegotiationStrategyConfig {
    ... on ABTestConfigType {
      type
      variants {
        strategyId
        weight
      }
      assignmentMethod
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on AcceptUpToMaxConfigType {
      type
      fallbackStrategyId
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on ConditionalConfigType {
      type
      rules {
        strategyId
        conditions {
          carrierOfferOverMax
          offerRangePercentAtLeast
          carrierStoppedBudging
          commodityPatterns
          equipmentTypes
          carrierEquipmentTypes
          targetPriceAtLeast
          targetPriceAtMost
          maxPriceAtLeast
          maxPriceAtMost
          hotLoad {
            offerCountThreshold
            timeWindowMinutes
          }
          inquiryCountAtLeast
          inquiryCountAtMost
          inquiryTimeWindowMinutes
          offerCountAtLeast
          offerCountAtMost
          offerTimeWindowMinutes
          hasAnyOfferAbove
          hasAnyOfferBelow
          averageOfferAbove
          averageOfferBelow
          transferCountAtLeast
          transferCountAtMost
          transferTimeWindowMinutes
          hasRateGuidanceForMethod
          rateGuidanceAtLeast
          rateGuidanceAtMost
          pickupDateWithin
          pickupWithinHours
          pickupEndDateWithin
          pickupEndWithinHours
          rejectionCountAtLeast
          rejectionCountAtMost
          offerGreaterThanMaxPlus
        }
      }
      defaultStrategyId
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on EscalationConfigType {
      type
      escalationThreshold
      initialIncrementAmount
      maxEscalationOverage
      timeoutSeconds
      escalationMessage
      includeFields
      escalationChannel
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on FixedAmountConfigType {
      type
      incrementAmount
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
      incrementTiers {
        carrierOfferOverMax
        targetPriceAtLeast
        targetPriceAtMost
        incrementAmount
      }
    }
    ... on NeverBudgeConfigType {
      type
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on AlwaysAcceptConfigType {
      type
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on AlwaysDeclineConfigType {
      type
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on PercentageBasedConfigType {
      type
      incrementPercentage
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
    ... on SmartPricingConfigType {
      type
      failCostConstant
      maxSteps
      alphaPrior
      betaPrior
      timeDecay
      offerSamplingRate
      expectedOffersPerTruckToLoad
      requiresApprovalMessage
      firstOfferBelowTargetCounterDollars
      firstOfferBelowTargetCounterPercent
      autoRejectThreshold
      autoRejectThresholdPercent
      targetBuffer
      targetBufferPercent
      carrierNoBudgeBehavior
    }
  }
`;

const GET_NEGOTIATION_STRATEGIES = gql`
  ${NEGOTIATION_STRATEGY_CONFIG_FRAGMENT}
  query GetNegotiationStrategies($input: NegotiationStrategiesInput) {
    negotiationStrategies(input: $input) {
      id
      name
      type
      active
      config {
        ...NegotiationStrategyConfigFields
      }
    }
  }
`;

const CREATE_NEGOTIATION_STRATEGY = gql`
  ${NEGOTIATION_STRATEGY_CONFIG_FRAGMENT}
  mutation CreateNegotiationStrategy($input: CreateNegotiationStrategyInput!) {
    createNegotiationStrategy(input: $input) {
      negotiationStrategy {
        id
        name
        type
        active
        config {
          ...NegotiationStrategyConfigFields
        }
      }
    }
  }
`;

const UPDATE_NEGOTIATION_STRATEGY = gql`
  ${NEGOTIATION_STRATEGY_CONFIG_FRAGMENT}
  mutation UpdateNegotiationStrategy($input: UpdateNegotiationStrategyInput!) {
    updateNegotiationStrategy(input: $input) {
      negotiationStrategy {
        id
        name
        type
        active
        config {
          ...NegotiationStrategyConfigFields
        }
      }
    }
  }
`;

const DELETE_NEGOTIATION_STRATEGY = gql`
  mutation DeleteNegotiationStrategy($input: DeleteNegotiationStrategyInput!) {
    deleteNegotiationStrategy(input: $input) {
      success
      referencingSOPs {
        id
        name
      }
      referencingStrategies {
        id
        name
      }
    }
  }
`;

export const useNegotiationStrategies = ({
  organizationId,
}: UseNegotiationStrategiesOptions): UseNegotiationStrategiesState => {
  const { data, loading, error, refetch } = useQuery(
    GET_NEGOTIATION_STRATEGIES,
    {
      variables: { input: { organizationId } },
      skip: !organizationId,
    }
  );

  return {
    strategies: data?.negotiationStrategies ?? [],
    loading,
    error,
    refetch,
  };
};

type CreateNegotiationStrategyInput = {
  organizationId: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
  active?: boolean;
};

type DeleteResult = {
  success: boolean;
  referencingSOPs?: { id: string; name: string }[];
  referencingStrategies?: { id: string; name: string }[];
};

export const useCreateNegotiationStrategy = () => {
  const [mutate, { loading }] = useMutation(CREATE_NEGOTIATION_STRATEGY);

  const createStrategy = async (input: CreateNegotiationStrategyInput) => {
    const result = await mutate({
      variables: { input },
      refetchQueries: ['GetNegotiationStrategies'],
    });
    return result.data?.createNegotiationStrategy?.negotiationStrategy;
  };

  return { createStrategy, loading };
};

type UpdateNegotiationStrategyInput = {
  id: string;
  organizationId: string;
  name?: string;
  type?: string;
  config?: Record<string, unknown>;
  active?: boolean;
};

export const useUpdateNegotiationStrategy = () => {
  const [mutate, { loading }] = useMutation(UPDATE_NEGOTIATION_STRATEGY);

  const updateStrategy = async (input: UpdateNegotiationStrategyInput) => {
    const result = await mutate({
      variables: { input },
      refetchQueries: ['GetNegotiationStrategies'],
    });
    return result.data?.updateNegotiationStrategy?.negotiationStrategy;
  };

  return { updateStrategy, loading };
};

type DeleteNegotiationStrategyInput = {
  id: string;
  organizationId: string;
};

export const useDeleteNegotiationStrategy = () => {
  const [mutate, { loading }] = useMutation(DELETE_NEGOTIATION_STRATEGY);

  const deleteStrategy = async (
    input: DeleteNegotiationStrategyInput
  ): Promise<DeleteResult> => {
    const result = await mutate({
      variables: { input },
      refetchQueries: ['GetNegotiationStrategies'],
    });
    return result.data?.deleteNegotiationStrategy;
  };

  return { deleteStrategy, loading };
};
